// @flow
/* global window */
/* global document */

import * as React from 'react';
import type { FormProps } from 'redux-form';

import clsx from 'clsx';

import type { Organization } from '@gandi/redux-sharing';
import { Alert, Base, Button, Hide, TogglePill, Svg } from '@gandi/ui';
import { Action, Divider, ListRow } from '@gandi/ui/deprecated';

import {
  Fieldset,
  Form,
  FormField,
  InputTextField,
  Label,
  TextAreaField,
  ToggleGroupField,
  SelectField,
} from 'gandi.react-form';
import type {
  TranslatorProps,
  TranslatorFunctionReturn,
  HtmlTranslatorFunctionOptions,
} from '@gandi/translate';

import type { WithRouterProps } from 'src/helpers/withRouter';
import type { ComponentStatus } from 'src/types';

import { FilterableSelect, SimpleSelect } from '../../../../../SelectComponent';

import type { IValues, Subject } from '../../../types';

import styles from './ContactSupportForm.scss';

export type StateProps = {|
  contractsUrl: string,
  disabledTips: Array<string>,
  /* The status of the current category (from the route parameters) */
  status: ComponentStatus,
  statusUrl: string,
  subjects: Array<{| text: string, value: string, hidden: boolean |}>,
  tips: {|
    [key: string]: Array<{|
      type: React.ElementConfig<typeof Alert>['intent'],
      message: string | {| __html: string |},
      title?: string,
    |}>,
  |},
  topics: {|
    [key: string]: Array<{| text: string, value: string |}>,
  |},
  rangePortfolio: Array<{| text: string, value: string |}>,
|};

export type DispatchProps = {|
  fetchStatus: () => any,
|};
export type OwnProps = {|
  isAuthenticatedUser: boolean,
  isCorporate: boolean,
  isNotForFqdn: boolean,
  isForIp: boolean,
  isStandalone: boolean,
  onSubmit: (values: IValues) => Promise<mixed>,
  personalDetails: Organization,
  reduxFormProps: FormProps,
  routeParams: Partial<{|
    category?: string,
    subject?: Subject,
    topic: string,
  |}>,
|};

type InjectedProps = {|
  ...WithRouterProps<{| category: string, subject?: Subject, topic?: string |}>,
  translator: TranslatorProps,
|};
type State = {|
  postmasterErrorSelected: string,
|};

export type Props = {|
  ...DispatchProps,
  ...InjectedProps,
  ...OwnProps,
  ...StateProps,
|};
class ContactSupportForm extends React.Component<Props, State> {
  state: State = {
    postmasterErrorSelected: '',
  };

  componentDidMount() {
    this.props.fetchStatus();
    window.setTimeout(() => {
      // Fix race condition
      let formElt = null;
      if (
        window.location.hash &&
        document.getElementById(window.location.hash)
      ) {
        formElt = document.getElementById(window.location.hash);
      } else if (!!this.props.routeParams.subject && !this.props.isStandalone) {
        formElt = document.getElementById('contact-support-form');
      }
      if (formElt && 'scrollIntoView' in formElt) {
        formElt.scrollIntoView();
      }
    }, 0);
  }

  hasSubCategory: (nextCategory?: string) => boolean = nextCategory => {
    const { isCorporate, routeParams, topics } = this.props;
    const category = nextCategory || routeParams.subject;

    return !isCorporate && (!category || !!topics[category]);
  };

  getHtmlHelpText: () => TranslatorFunctionReturn<HtmlTranslatorFunctionOptions> | null =
    () => {
      if (this.props.routeParams.subject !== 'my-rights') {
        return null;
      }

      const { __ } = this.props.translator;

      switch (this.props.routeParams.topic) {
        case 'article-15_access': {
          return __(
            '<p>Please provide us with a detailed request, ' +
              'stipulating the Articles concerned (Article 15.1 to 15.3).</p>',
            { withHTML: true }
          );
        }
        case 'article-16_rectification': {
          return __(
            '<p>' +
              'The rectification of your data is done online and can ' +
              'be done by yourself at any time from your account. ' +
              'You will find detailed instructions in how to do this in our ' +
              '<a href="https://docs.gandi.net/en/account_management/' +
              'changing_account_information/index.html">documentation</a>.' +
              '</p>\n\n<p>' +
              'If you lost your login credentials, please ' +
              '<a href="../">contact our customer support service</a>.' +
              '</p>',
            { withHTML: true }
          );
        }
        case 'article-17_erasure': {
          return __(
            '<p>' +
              '<strong>Warning:</strong> in order for your request to be ' +
              'valid, please cite one of the cases applicable to your request,' +
              ' in conformity to Article 17.1.a to f of the RGPD.' +
              '</p>\n\n<p>' +
              'For legal reasons, we can only take action on such a request ' +
              'for erasure after a delay of one (1) year following the ' +
              'expiration of the last service attached to your account.' +
              '</p>',
            { withHTML: true }
          );
        }
        case 'article-18_restriction_of_processing': {
          return __(
            '<p>' +
              '<strong>Warning:</strong> in order for your request to be ' +
              'valid, please cite one of the cases applicable to your request,' +
              ' in conformity to Article 18.1.a to d of the RGPD.' +
              '</p>\n\n<p>' +
              'For legal reasons, we can only take action on such a request ' +
              'for erasure after a delay of one (1) year following the ' +
              'expiration of the last service attached to your account.' +
              '</p>',
            { withHTML: true }
          );
        }
        case 'article-20_data_portability': {
          return __(
            '<p>' +
              'Portability only applies to certain cases of domain name ' +
              'transfer (depending on the extension). In order to achieve this' +
              ' you need to provide the authorization key of the domain name ' +
              'in question to your new provider, and Gandi will provide' +
              'to them the personal data on record in order for the transfer ' +
              'to be performed.' +
              '<br />\n' +
              'For more information on the domain name transfer process, ' +
              'please see our <a href="https://docs.gandi.net/en/domain_names/' +
              'transfer_out/index.html">transfer documentation</a>.' +
              '</p>\n\n<p>' +
              'You may consult our "Personal Data Protection Policy" ' +
              'which is accessible from our ' +
              '<a href="%(contracts_url)s">Contrats page</a>, ' +
              'to learn how Gandi handles your personal data.' +
              '</p>',
            { contracts_url: this.props.contractsUrl, withHTML: true }
          );
        }
        case 'article-21_objection': {
          return __(
            '<p>' +
              'You can not object to the processing of your data, ' +
              'with the exception of the cancellation of your contracts, ' +
              'since the processing of it is necessary for the providing ' +
              'of the service.' +
              '<br />\n' +
              'Any data profiling performed is necessary for the detection ' +
              'of potential fraud and protect you notably in the event  ' +
              'that your account would be hacked.' +
              '</p>\n\n<p>' +
              'For any request concerning the cancellation of your contracts, ' +
              'please <a href="../">contact our customer service</a>.' +
              '</p>\n\n<p>' +
              'You may consult our "Personal Data Protection Policy" ' +
              'which is accessible from our ' +
              '<a href="%(contracts_url)s">Contrats page</a>, ' +
              'to learn how Gandi handles your personal data.' +
              '</p>\n\n<p>' +
              'In that which concerns direct marketing, you may at any time ' +
              'change this setting online from your account, ' +
              'so as to no longer receive our newsletters.' +
              '</p>',
            { contracts_url: this.props.contractsUrl, withHTML: true }
          );
        }
        default: {
          return null;
        }
      }
    };

  onChangeSubject: (evt: SyntheticEvent<HTMLInputElement>) => mixed = evt => {
    this.props.router.push({
      pathname: `/contact/${evt.currentTarget.value}`,
      state: { noTrack: this.hasSubCategory(evt.currentTarget.value) },
    });

    // Refresh status
    this.props.fetchStatus();
  };

  onChangeTopic: (evt: SyntheticEvent<HTMLInputElement>) => mixed = evt => {
    if (this.props.routeParams.subject) {
      this.props.router.push(
        `/contact/${this.props.routeParams.subject}/${evt.currentTarget.value}`
      );
    }

    // Refresh status
    this.props.fetchStatus();
  };

  renderTips: (
    tips: {|
      message: {| __html: string |} | string,
      title?: string,
      type: React.ElementConfig<typeof Alert>['intent'],
    |},
    index: number
  ) => React.Element<typeof Base> = (
    tips: {|
      message: {| __html: string |} | string,
      title?: string,
      type: React.ElementConfig<typeof Alert>['intent'],
    |},
    index: number
  ) => {
    const { __ } = this.props.translator;

    return (
      <Base className={styles.row} key={`tips-${index}`}>
        <Alert
          className={styles.alert}
          message={tips.message}
          title={tips.title || __('Tips')}
          intent={tips.type}
        />
      </Base>
    );
  };

  render(): React.Element<typeof Form> {
    const {
      translator: { __ },
      disabledTips,
      isCorporate,
      isNotForFqdn,
      isForIp,
      onSubmit,
      rangePortfolio,
      reduxFormProps: { error, handleSubmit },
      routeParams,
      subjects,
      tips,
      topics,
    } = this.props;

    const isFeedback =
      routeParams.subject === 'feedback' ||
      (routeParams.topic && routeParams.topic.endsWith('feedback'));

    const showDomainFields =
      !isFeedback &&
      routeParams.subject !== 'migration' &&
      routeParams.subject !== 'feedback' &&
      routeParams.subject !== 'hosting' &&
      routeParams.subject !== 'reseller-contact' &&
      routeParams.subject !== 'corporate-contact' &&
      routeParams.subject !== 'my-rights' &&
      routeParams.subject !== 'vps';

    const showHostingFields = routeParams.subject === 'hosting' && !isFeedback;
    const showVPSFields = routeParams.subject === 'vps' && !isFeedback;
    const showMigrationFields = routeParams.subject === 'migration';
    const showCorporateResellerContactFields =
      routeParams.subject === 'corporate-contact' ||
      routeParams.subject === 'reseller-contact';
    const showMyRightsFields = routeParams.subject === 'my-rights';
    const showEmailSendingAndReceivingFields =
      routeParams.topic === 'mail-sending-problems' ||
      routeParams.topic === 'mail-receiving-problems';

    const errorMessagesFromPostmaster = [
      {
        text: 'Reject for policy reason. RULE1_1',
        value: 'Reject for policy reason. RULE1_1',
      },
      {
        text: 'Reject for policy reason. RULE2_1',
        value: 'Reject for policy reason. RULE2_1',
      },
      {
        text: 'Reject for policy reason. RULE2_2',
        value: 'Reject for policy reason. RULE2_2',
      },
      {
        text: 'Reject for policy reason. RULE3_1',
        value: 'Reject for policy reason. RULE3_1',
      },
      {
        text: 'Reject for policy reason. RULE3_2',
        value: 'Reject for policy reason. RULE3_2',
      },
      {
        text: 'Greylisted for %s seconds',
        value: 'Greylisted for %s seconds',
      },
      {
        text: 'sasl_rate_limit per %',
        value: 'sasl_rate_limit per %',
      },
      {
        text: 'blocked you maybe infected',
        value: 'blocked you maybe infected',
      },
      {
        text: 'blocked using sbl-xbl.spamhaus.org',
        value: 'blocked using sbl-xbl.spamhaus.org',
      },
      {
        text: __('Other'),
        value: 'other',
      },
    ];

    const componentStatus: ?ComponentStatus = this.props.status;

    const showForm =
      (!!routeParams.subject &&
        (!!routeParams.topic || !topics[routeParams.subject])) ||
      isCorporate;

    const sortByText = (
      from: {| [string]: string |},
      next: {| [string]: string |}
    ) => {
      if (from.text < next.text) {
        return -1;
      }
      if (from.text > next.text) {
        return 1;
      }
      return 0;
    };

    const sortSubCat = (
      items: $ReadOnlyArray<{| [string]: string |}>
    ): $ReadOnlyArray<{| [string]: string |}> => {
      let result = [...items].sort(sortByText);

      const feedback = result.find(element =>
        element.value.endsWith('feedback')
      );
      const other = result.find(element => element.value.endsWith('other'));

      result = result
        .filter(item => !item.value.endsWith('feedback'))
        .filter(item => !item.value.endsWith('other'));

      if (feedback) {
        result.push(feedback);
      }
      if (other) {
        result.push(other);
      }

      return result;
    };

    const isSubCategoryDisabled =
      !routeParams.subject || !topics[routeParams.subject];

    const routeParamsTopic = routeParams.topic;

    return (
      <Form
        method="post"
        onSubmit={handleSubmit(onSubmit)}
        className={clsx(styles.form, 'form-stacked')}
      >
        {!this.props.isStandalone && (
          <Base
            backgroundColor="grey-lighter"
            separatorBottom
            className={styles['form-column']}
          >
            <Fieldset>
              <Label target="category" text={__('Nature of your question')} />

              <Base flex>
                <FormField
                  component={SimpleSelect}
                  id="category"
                  name="category"
                  onChange={this.onChangeSubject}
                  placeholder={__('Select subject')}
                  disabled={this.props.isStandalone}
                  values={subjects.filter(
                    option =>
                      option.value === routeParams.subject || !option.hidden
                  )}
                  variant={['small', 'block']}
                  // this prop is used when a user share an url
                  defaultValue={routeParams.subject}
                />
                {!routeParams.subject ? (
                  <Button
                    className={styles.nojsbutton}
                    name="choose_subject"
                    small
                    type="submit"
                  >
                    {__('Ok')}
                  </Button>
                ) : (
                  <Svg
                    className={styles.modeSvg}
                    name="checkmark"
                    theme="success"
                  />
                )}
              </Base>
            </Fieldset>
            {this.hasSubCategory() ? (
              <Fieldset
                className={isSubCategoryDisabled ? styles.disabled : undefined}
              >
                <Label target="sub_category" text={__('Category')} />
                <Base flex>
                  <FormField
                    component={SimpleSelect}
                    disabled={isSubCategoryDisabled}
                    id="sub_category"
                    name="sub_category"
                    onChange={this.onChangeTopic}
                    placeholder={
                      showMyRightsFields
                        ? __('Choose the subject')
                        : __('Select topic')
                    }
                    values={sortSubCat(
                      routeParams.subject && topics[routeParams.subject]
                        ? topics[routeParams.subject]
                        : []
                    )}
                    variant={['small', 'block']}
                    // this prop is used when a user share an url
                    defaultValue={routeParams.topic}
                  />
                  {!routeParams.subject && (
                    <Button
                      className={styles.nojsbuttonHidden}
                      disabled
                      small
                      aria-hidden="true"
                      tabIndex="-1"
                    >
                      {__('Ok')}
                    </Button>
                  )}
                  {routeParams.subject &&
                    (!routeParams.topic ? (
                      <Button
                        className={styles.nojsbutton}
                        name="choose_topic"
                        small
                        type="submit"
                      >
                        {__('Ok')}
                      </Button>
                    ) : (
                      <Svg
                        className={styles.modeSvg}
                        name="checkmark"
                        theme="success"
                      />
                    ))}
                </Base>
              </Fieldset>
            ) : null}
          </Base>
        )}

        {this.props.isStandalone && (
          <input name="category" type="hidden" value={routeParams.subject} />
        )}

        {this.props.status &&
          componentStatus !== 'operational' &&
          !this.props.isStandalone && (
            <Base marginBottom="base">
              <ListRow separatorBottom>
                <Alert
                  data-testid="alert"
                  title={
                    undefined
                    /* We use a fake `message` prop as a title until UI supports
                    optional `message` prop in Alert. */
                  }
                  message={
                    <h3 style={{ marginBottom: 0 }}>
                      {componentStatus === 'under_maintenance'
                        ? __('A maintenance is in progress right now')
                        : __('An incident is in progress right now')}
                    </h3>
                  }
                  intent={
                    // eslint-disable-next-line no-nested-ternary
                    componentStatus === 'under_maintenance'
                      ? 'info'
                      : componentStatus !== 'degraded_performance'
                      ? // partial_outage || major_outage
                        'danger'
                      : // degraded_performance
                        'warning'
                  }
                  flexGrow1
                />
                <Action
                  icon="arrowRight"
                  href={this.props.statusUrl}
                  variant="wide"
                  theme={
                    // eslint-disable-next-line no-nested-ternary
                    componentStatus === 'major_outage'
                      ? 'danger-plain'
                      : componentStatus === 'partial_outage'
                      ? 'danger'
                      : undefined
                  }
                >
                  {__('See Status')}
                </Action>
              </ListRow>
            </Base>
          )}

        <Base>
          {this.hasSubCategory() && this.props.isStandalone ? (
            <Divider
              direction="columnToRow"
              spread="equal"
              backgroundColor="grey-lighter"
            >
              <Label
                className={styles.label}
                target="sub_category"
                text={showMyRightsFields ? __('GDPR rights') : __('Category')}
              />

              <Base className={styles.select}>
                <FormField
                  component={SimpleSelect}
                  disabled={isSubCategoryDisabled}
                  id="sub_category"
                  name="sub_category"
                  onChange={this.onChangeTopic}
                  placeholder={
                    showMyRightsFields
                      ? __('Choose the subject')
                      : __('Select topic')
                  }
                  values={sortSubCat(
                    routeParams.subject && topics[routeParams.subject]
                      ? topics[routeParams.subject]
                      : []
                  )}
                  variant={['small', 'block']}
                  // this prop is used when a user share an url
                  defaultValue={routeParams.topic}
                />
                {!routeParams.subject && (
                  <Button
                    className={styles.nojsbuttonHidden}
                    disabled
                    small
                    aria-hidden="true"
                    tabIndex="-1"
                  >
                    {__('Ok')}
                  </Button>
                )}
                {routeParams.subject &&
                  (!routeParams.topic ? (
                    <Button
                      className={styles.nojsbutton}
                      name="choose_topic"
                      small
                      type="submit"
                    >
                      {__('Ok')}
                    </Button>
                  ) : (
                    <Svg
                      className={styles.modeSvg}
                      name="checkmark"
                      theme="success"
                    />
                  ))}
              </Base>
            </Divider>
          ) : null}
          {!!routeParamsTopic &&
            disabledTips.indexOf(routeParams.subject) < 0 &&
            !!tips[routeParamsTopic] &&
            tips[routeParamsTopic]
              // remove not translated keys
              .filter(item => {
                /* eslint-disable no-underscore-dangle */
                // $FlowFixMe[incompatible-type]
                const msg: string = item.message.__html || item.message;
                /* eslint-enable no-underscore-dangle */
                return routeParams.topic && msg.indexOf(routeParams.topic) < 0;
              })
              .map(this.renderTips)}
          {showForm && (
            <Base
              backgroundColor="grey-lighter"
              className={styles['form-column']}
            >
              {error && (
                <Base marginBottom="base">
                  <Alert message={error} title={__('Error')} intent="danger" />
                </Base>
              )}
              {isCorporate ? (
                <Base className={styles.fullWidth}>
                  <SelectField
                    id="type"
                    key="sub_category"
                    name="sub_category"
                    onChange={this.onChangeTopic}
                    placeholder={__('Select a category')}
                    values={routeParams.subject && topics[routeParams.subject]}
                    variant="block"
                    label={__('Category')}
                    required
                  />
                </Base>
              ) : null}
              <InputTextField
                disabled={
                  showMyRightsFields &&
                  this.props.isAuthenticatedUser &&
                  !!this.props.personalDetails.firstname
                }
                key="firstname"
                name="firstname"
                label={__('First name')}
                required={!isFeedback}
              />
              <InputTextField
                disabled={
                  showMyRightsFields &&
                  this.props.isAuthenticatedUser &&
                  !!this.props.personalDetails.lastname
                }
                key="lastname"
                name="lastname"
                label={__('Last name')}
                required={!isFeedback}
              />

              {this.props.isAuthenticatedUser && !this.props.isStandalone && (
                <Hide nojs>
                  <Fieldset key="sharing_id">
                    <Label
                      target="sharing_id"
                      text={__('I am writing on behalf of:')}
                    />
                    <FormField
                      component={FilterableSelect}
                      id="sharing_id"
                      name="sharing_id"
                      reduxFormProps={this.props.reduxFormProps}
                    />
                    <div hidden>
                      {/* html input so redux-form won't recognize the field */}
                      {/* This aims to submit "sharing_id" in no-js */}
                      <input
                        key="sharing_id"
                        name="sharing_id"
                        type="hidden"
                        value={
                          this.props.personalDetails &&
                          this.props.personalDetails.id
                        }
                      />
                      <InputTextField
                        key="orgname"
                        name="orgname"
                        type="hidden"
                      />
                      <InputTextField
                        key="reseller"
                        id="reseller"
                        name="reseller"
                        type="hidden"
                      />
                    </div>
                  </Fieldset>
                </Hide>
              )}

              {((!this.props.isAuthenticatedUser && !showMyRightsFields) ||
                !!showCorporateResellerContactFields) && (
                <InputTextField
                  key="orgname"
                  name="orgname"
                  label={__('Company name')}
                  required={this.props.isStandalone}
                />
              )}

              {!!showMyRightsFields && (
                <InputTextField
                  disabled={this.props.isAuthenticatedUser}
                  key="username"
                  name={
                    this.props.isAuthenticatedUser
                      ? 'username'
                      : 'params.username'
                  }
                  label={__('Username (or handle)')}
                />
              )}

              {showCorporateResellerContactFields && [
                <Fieldset key="portfolio_size">
                  <Label
                    target="portfolio_size"
                    text={
                      routeParams.subject === 'corporate-contact'
                        ? __('Portfolio Size')
                        : __('Number of Domains')
                    }
                  />
                  <FormField
                    component={SimpleSelect}
                    id="portfolio_size"
                    name="params.portfolio"
                    placeholder={__('Select a range')}
                    values={rangePortfolio}
                    variant="block"
                  />
                </Fieldset>,
                <InputTextField
                  key="country"
                  name="params.country"
                  label={__('Country')}
                />,
                <InputTextField
                  key="phone"
                  name="params.phone"
                  label={__('Phone Number')}
                  type="tel"
                  required
                />,
              ]}

              <InputTextField
                disabled={
                  showMyRightsFields &&
                  this.props.isAuthenticatedUser &&
                  !!this.props.personalDetails.email
                }
                key="reply_to"
                name="reply_to"
                label={__('E-mail address for corresponding')}
                type="email"
                required
              />

              {showCorporateResellerContactFields && (
                <InputTextField
                  key="corporate_role"
                  name="params.role"
                  label={__('Role')}
                />
              )}

              {showDomainFields && (
                <ToggleGroupField
                  key="for_fqdn"
                  name="_for_fqdn"
                  label={__('Does it concern a specific domain?')}
                  required
                >
                  <TogglePill value="true">{__('Yes')} </TogglePill>
                  <TogglePill value="false">{__('No')}</TogglePill>
                </ToggleGroupField>
              )}

              {showDomainFields && (
                <InputTextField
                  key="domainname"
                  disabled={isNotForFqdn}
                  name="params.domain"
                  label={
                    routeParams.subject === 'shs'
                      ? __('Website')
                      : __('Domain name')
                  }
                  required={!isNotForFqdn}
                />
              )}

              {showDomainFields && !showEmailSendingAndReceivingFields && (
                <InputTextField
                  key="session_number"
                  name="session_number"
                  label={__('Transaction number')}
                />
              )}

              {showEmailSendingAndReceivingFields && [
                <InputTextField
                  key="error_start_date"
                  name="params.error_start_date"
                  label={__('When or how did this problem start:')}
                  required
                />,
                <InputTextField
                  key="application_used"
                  name="params.application_used"
                  label={__('What email or webmail application are you using?')}
                  required
                />,
              ]}

              {(showHostingFields || showVPSFields) && [
                <ToggleGroupField
                  key="noip"
                  name="_noip"
                  label={
                    showHostingFields
                      ? __('Do you already have Gandi cloud resources?')
                      : __('Do you already have GandiCloud VPS resources?')
                  }
                  required
                >
                  <TogglePill value="truenoip">{__('Yes')}</TogglePill>
                  <TogglePill value="falsenoip">{__('No')}</TogglePill>
                </ToggleGroupField>,
                <InputTextField
                  disabled={!isForIp}
                  key="ip"
                  name="params.ip"
                  label={__('IP address of the server')}
                  required={isForIp}
                />,

                <ToggleGroupField
                  key="blocked"
                  name="blocked"
                  label={__('Is your server frozen or not responding?')}
                  required
                >
                  <TogglePill value="true">{__('Yes')}</TogglePill>
                  <TogglePill value="false">{__('No')}</TogglePill>
                </ToggleGroupField>,
                <InputTextField
                  key="session_number"
                  name="session_number"
                  label={__('Transaction number')}
                />,
              ]}

              {showMigrationFields && (
                <InputTextField
                  key="handle"
                  name="params.handle"
                  label={__('Gandi handle')}
                  required
                />
              )}
            </Base>
          )}
          {showEmailSendingAndReceivingFields && (
            <Base
              backgroundColor="grey-lighter"
              paddingRight="small"
              paddingLeft="small"
              paddingBottom="small"
            >
              <SelectField
                id="problem_type"
                name="params.error_type"
                placeholder={__('Select an error message')}
                values={errorMessagesFromPostmaster}
                onChange={(evt: SyntheticInputEvent<>) => {
                  this.setState({ postmasterErrorSelected: evt.target.value });
                }}
                variant="block"
                label={__('Problem type')}
                required
              />
              {this.state.postmasterErrorSelected === 'other' && (
                <TextAreaField
                  name="params.error_message"
                  label={__(
                    'Copy and paste the error message that you or the sender is getting here:'
                  )}
                  required
                  rows={3}
                />
              )}
            </Base>
          )}

          {((!!routeParams.subject &&
            (!!routeParams.topic || !topics[routeParams.subject])) ||
            isCorporate) && (
            <Base
              backgroundColor="grey-lighter"
              padding="small"
              className={styles.oneFieldFullRow}
            >
              <TextAreaField
                label={__('Message')}
                name="message"
                placeholder={
                  // eslint-disable-next-line no-nested-ternary
                  showMyRightsFields
                    ? __(
                        'Please provide us with a detailed request, ' +
                          'stipulating the relevant GDPR Articles.'
                      )
                    : showCorporateResellerContactFields
                    ? __(
                        'If you have a project, a specific request or if ' +
                          "you would like a price quote, don't forget to " +
                          'precise it.'
                      )
                    : __(
                        'Describe your problem and the context as precisely ' +
                          'as possible, and provide us any error message you ' +
                          'have encountered.'
                      )
                }
                required
                rows={12}
              />
              {/* "Help" text below the textarea (for 'my-rights' category)
                 until react-form fields' `help` prop can handle __html. */}
              <Base
                className="form-help"
                dangerouslySetInnerHTML={this.getHtmlHelpText()}
                margin="none"
                tagName="div"
              />
            </Base>
          )}
          {(!!routeParams.subject &&
            (!!routeParams.topic || !topics[routeParams.subject])) ||
          isCorporate ? (
            <Base
              flex
              flexGrow1
              marginBottom="medium"
              marginTop="base"
              flexJustifyEnd
            >
              <Button type="submit" grouped>
                {__('Send')}
              </Button>
            </Base>
          ) : null}
        </Base>
      </Form>
    );
  }
}

export default ContactSupportForm;
